import React, {
    ComponentType,
    MutableRefObject,
    useContext,
    useEffect,
    useMemo,
    useRef
} from 'react'

export type ScrollContext = {
    scrollTo: (id: string) => void
    register: (ref: MutableRefObject<any>, id: string) => () => void
}

const Ctx = React.createContext<ScrollContext>(null as any)

class Scroller implements ScrollContext {
    private refs = new Map<string, MutableRefObject<any>>()

    constructor(
        private offset: number,
        private window: Window
    ) {}

    private getRef = (id: string): MutableRefObject<any> => {
        const x: MutableRefObject<any> | undefined = this.refs.get(id)
        if (!x) throw new Error(`Ref ${id} not registered`)
        return x
    }

    register = (ref: MutableRefObject<any>, id: string) => {
        this.refs.set(id, ref)

        return () => {
            this.refs.delete(id)
        }
    }

    hasRegistered = (id: string) => this.refs.has(id)

    scrollTo = (id: string): void => {
        if (!this.hasRegistered(id))
            throw new Error(
                'Does tot have registered ScrollPoint with id ' + id
            )
        this.getRef(id).current.scrollIntoView()

        const offset = this.window.scrollY - this.offset

        this.window.scroll(0, offset)
    }
}

export const ScrollContextProvider: ComponentType<{
    offset: number
    children: React.ReactNode
}> = ({ offset, children }) => {
    const obj = useMemo(() => {
        return new Scroller(offset, window)
    }, [])

    // @ts-ignore TODO debugging, later remove
    window.sctx = obj
    return <Ctx.Provider value={obj}>{children}</Ctx.Provider>
}

const getCtx = (): ScrollContext => {
    const ctx = useContext(Ctx)

    if (!ctx) throw new Error('Not in scrolling context')

    return ctx
}

export const useScrollTo = () => {
    const ctx = getCtx()
    return (id: string) => {
        ctx.scrollTo(id)
    }
}

const useRegisterScroll = (id: string): MutableRefObject<any> => {
    const ref = useRef()
    const ctx = getCtx()

    useEffect(() => {
        const unregister = ctx.register(ref, id)

        return unregister
    }, [])
    return ref
}
export const ScrollPoint: ComponentType<{ id: string }> = ({ id }) => {
    const ref = useRegisterScroll(id)
    return (
        <span
            style={{
                visibility: 'hidden'
            }}
            ref={ref}
        />
    )
}
